"use client";

import { Api } from "@/app/api";

export type CalendarStatus = {
	connected: boolean;
	timezone: string;
	slotMinutes: number;
};

export type CalendarSlot = {
	start: string;
	end: string;
	label: string;
};

export type CalendarSlotsResponse = {
	timezone: string;
	slotMinutes: number;
	slots: CalendarSlot[];
};

export type CalendarConnectionStatus = {
	connected: boolean;
	email: string | null;
	timezone: string;
	workStart: string;
	workEnd: string;
};

export const calendarInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getCalendarStatus: builder.query<CalendarStatus, void>({
			query: () => "/calendar/status",
			providesTags: [{ type: "calendar", id: "STATUS" }],
		}),
		getCalendarSlots: builder.query<CalendarSlotsResponse, { from: string; to: string }>({
			query: ({ from, to }) => ({
				url: "/calendar/slots",
				params: { from, to },
			}),
			providesTags: [{ type: "calendar", id: "SLOTS" }],
		}),
		bookCalendarCall: builder.mutation<
			{ booked: boolean; meetLink: string | null },
			{
				start: string;
				end: string;
				firstName: string;
				lastName: string;
				email: string;
				notes?: string;
				serviceTitle?: string;
				sourceUrl?: string;
			}
		>({
			query: (body) => ({
				url: "/calendar/book",
				method: "POST",
				body,
			}),
			invalidatesTags: [{ type: "calendar", id: "SLOTS" }],
		}),
		getCalendarConnection: builder.query<CalendarConnectionStatus, void>({
			query: () => "/calendar/connection",
			providesTags: [{ type: "calendar", id: "CONNECTION" }],
		}),
		updateCalendarHours: builder.mutation<
			CalendarConnectionStatus,
			{ workStart: string; workEnd: string }
		>({
			query: (body) => ({
				url: "/calendar/connection",
				method: "PATCH",
				body,
			}),
			invalidatesTags: [
				{ type: "calendar", id: "CONNECTION" },
				{ type: "calendar", id: "SLOTS" },
			],
		}),
		connectCalendar: builder.mutation<{ connected: boolean; email: string }, { code: string }>({
			query: (body) => ({
				url: "/calendar/connect",
				method: "POST",
				body,
			}),
			invalidatesTags: [
				{ type: "calendar", id: "CONNECTION" },
				{ type: "calendar", id: "STATUS" },
				{ type: "calendar", id: "SLOTS" },
			],
		}),
		disconnectCalendar: builder.mutation<{ connected: boolean }, void>({
			query: () => ({
				url: "/calendar/connection",
				method: "DELETE",
			}),
			invalidatesTags: [
				{ type: "calendar", id: "CONNECTION" },
				{ type: "calendar", id: "STATUS" },
				{ type: "calendar", id: "SLOTS" },
			],
		}),
	}),
});

export const {
	useGetCalendarStatusQuery,
	useGetCalendarSlotsQuery,
	useBookCalendarCallMutation,
	useGetCalendarConnectionQuery,
	useConnectCalendarMutation,
	useUpdateCalendarHoursMutation,
	useDisconnectCalendarMutation,
} = calendarInjection;
